import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { type BrandSummary, type ChatSessionResponse, listBrandsOptions } from "@/api";
import type { ChatComposerProps } from "./ChatComposer";
import { useOnChatSwitch } from "./useChatSwitchKey";

const byDefaultThenName = (a: BrandSummary, b: BrandSummary) =>
  Number(Boolean(b.isDefault)) - Number(Boolean(a.isDefault)) || a.name.localeCompare(b.name);

/**
 * The chat's brand: the composer's picker and the brand a new chat is created with. Offered only with two or more
 * brands (one is always the company default anyway), as frontend/'s BrandPicker, and never for Build with AI chats
 * (they make Build items, not branded output). A chat that exists before its first message (attaching a file creates
 * it) saves a pick to it with `setup`; once the chat has started its brand is shown read-only. The brand picked for a
 * new chat doesn't follow the person into another chat (`chatKey`, MHS-1039).
 */
export function useChatBrand({
  enabled,
  sessionUuid,
  session,
  started,
  setup,
  chatKey,
}: Readonly<{
  enabled: boolean;
  sessionUuid: string | undefined;
  session: Pick<ChatSessionResponse, "brandUuid" | "brandName"> | undefined;
  started: boolean;
  setup: Readonly<{ save: (change: { brandUuid: string | null }) => void }>;
  chatKey: number;
}>) {
  const [picked, setPicked] = useState<string | null>(null);
  useOnChatSwitch(chatKey, () => setPicked(null));
  const query = useQuery({
    ...listBrandsOptions({ query: { page_size: 100 } }),
    enabled,
    select: (r): BrandSummary[] => [...r.data].sort(byDefaultThenName),
  });
  const brands = query.data ?? [];
  const chosen = sessionUuid
    ? { selected: session?.brandUuid ?? null, onSelect: (brandUuid: string | null) => setup.save({ brandUuid }) }
    : { selected: picked, onSelect: setPicked };
  // Read-only once started: the chat's brand, or for a new chat whose first message is being sent, the one picked
  const startedWith = sessionUuid
    ? (session?.brandName ?? null)
    : (brands.find((b) => b.uuid === picked)?.name ?? null);
  const picker: ChatComposerProps["brand"] =
    enabled && brands.length >= 2 ? { brands, ...chosen, fixedName: started ? startedWith : undefined } : undefined;
  return {
    /** Sent when a new chat is created; null = the company default. */
    brandUuid: enabled && !sessionUuid ? picked : null,
    picker,
  };
}
